import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useAuth, useGet, usePost } from 'hooks';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import { disableList, enableList, getPaginatedLists } from 'services/api';
import { Pane, Table } from 'components/common';
import { useConfig } from './config';
import {
  getActiveModal,
  getIncludePausedLists,
  getIsLoading,
  getListsPageNumber,
  getListsPageSize,
  getListsTableSearch,
  getListsTableSort,
  getOnlyEnriched,
  getShowMyListsOnly,
  getView,
} from 'selectors/lists';
import {
  resetSelectedLists,
  setActiveList,
  setListsPageNumber,
  setListsPageSize,
  setListsSort,
  setModal,
  setView,
  toggleLists,
} from 'actions/lists';
import { Create } from 'components/common/Lists/Modals';
import Actions from './Actions';
import { views } from '../constants';
import {
  Edit,
  Delete,
  Export,
  BulkDelete,
  BulkDisable,
  PushToCrm,
} from '../Modals';
import Title from './Title';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledTable = styled(Table)`
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  margin-top: 10px;

  .header {
    :last-child {
      justify-content: flex-end;
    }
  }
`;

const modals = {
  create: Create,
  createAndAdd: Create,
  edit: Edit,
  delete: Delete,
  bulkDelete: BulkDelete,
  bulkDisable: BulkDisable,
  pushToCrm: PushToCrm,
  export: Export,
};

function Lists() {
  const dispatch = useDispatch();
  const view = useSelector(getView);
  const pageNumber = useSelector(getListsPageNumber);
  const pageSize = useSelector(getListsPageSize);
  const sort = useSelector(getListsTableSort);
  const includePausedLists = useSelector(getIncludePausedLists);
  const showMyListsOnly = useSelector(getShowMyListsOnly);
  const search = useSelector(getListsTableSearch);
  const onlyEnriched = useSelector(getOnlyEnriched);

  const activeModal = useSelector(getActiveModal);
  const { user } = useAuth();

  const [{ res: data, loading: fetchListsLoading }, fetchLists] = useGet({
    url: getPaginatedLists(view),
    query: {
      includeDisabled: includePausedLists,
      onlyShowMine: showMyListsOnly || user.isPartnerAccount,
      onlyShowEnrichedLists: onlyEnriched && !user.isPartnerAccount,
      pageSize: pageSize,
      page: pageNumber,
      orderBy: sort.by,
      order: sort.dir,
      search,
    },
    onMount: true,
  });

  const handleEditList = useCallback(
    list => {
      dispatch(setActiveList(list));
      dispatch(setModal('edit'));
    },
    [dispatch]
  );

  const [
    { res: toggleListResult, loading: toggleListLoading },
    toggleList,
  ] = usePost({});
  const isGlobalLoading = useSelector(getIsLoading);
  const loading = fetchListsLoading || toggleListLoading || isGlobalLoading;

  useEffect(() => {
    if (toggleListResult) {
      fetchLists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleListResult]);

  const handleToggleList = useCallback(
    list => {
      const url = list.enabled
        ? disableList(list.id, view)
        : enableList(list.id, view);
      toggleList({
        url: url,
      });
    },
    [toggleList, view]
  );

  const handleDeleteList = useCallback(
    list => {
      dispatch(setActiveList(list));
      dispatch(setModal('delete'));
    },
    [dispatch]
  );

  const crmTypeRef = useRef(null);
  const handlePushToCRM = useCallback(
    (crmType, list) => {
      dispatch(setActiveList(list, { clearSelectedMembers: false }));
      crmTypeRef.current = crmType;
      dispatch(setModal('pushToCrm'));
    },
    [dispatch]
  );

  const config = useConfig(
    view,
    data,
    loading,
    user,
    handleEditList,
    handleToggleList,
    handleDeleteList,
    handlePushToCRM
  );

  // handle old redirects
  const location = useLocation();
  const { id } = useParams();
  const { view: externalView, listId } = qs.parse(location.search);
  const { replace } = useHistory();
  useEffect(() => {
    if (id || listId) {
      const routeId = id || listId;
      replace(
        !externalView || externalView === views.researchers
          ? `/lists/researcher/${routeId}`
          : `/lists/organisation/${routeId}`
      );
      return;
    }

    if (externalView) {
      dispatch(setView(externalView));
      replace('/lists');
      return;
    }
  }, [externalView, id, replace, dispatch, listId]);
  if (externalView) {
    return null;
  }

  const handlePageSize = size => dispatch(setListsPageSize(size));

  const handlePageNumber = number => dispatch(setListsPageNumber(number));

  const handleSortChange = sortKey => {
    dispatch(setListsSort(sortKey));
  };

  const onModalClose = options => {
    dispatch(setModal(''));

    if (options?.fetchLists) {
      fetchLists();
      return;
    }

    if (options?.id && activeModal === 'create') {
      fetchLists();
      return;
    }

    if (options?.id && activeModal === 'edit') {
      fetchLists();
      return;
    }

    if (options?.deleteListSuccessfully) {
      fetchLists();
      if (options?.id) {
        dispatch(
          toggleLists({
            id: options.id,
            onlyIfSelected: true,
          })
        );
      }
      return;
    }

    if (options?.bulkDelete) {
      fetchLists();
      dispatch(resetSelectedLists());
    }
  };

  const Modal = modals[activeModal];
  return (
    <Pane
      title={
        <Title
          // now both academic and industry licensed users have access to org lists
          hasOrgLicense={true}
          loading={loading}
        />
      }
    >
      <Container>
        <Actions total={data?.total || 0} loading={loading} view={view} />
        <StyledTable
          paginate
          sortable
          config={config}
          data={data?.data || []}
          resultSize={data?.total || 0}
          pageSize={pageSize}
          pageNumber={pageNumber}
          onChangePageSize={handlePageSize}
          onChangePageNumber={handlePageNumber}
          onSortChange={handleSortChange}
          loading={loading}
          sort={sort.by}
          sortDir={sort.dir}
          pageSizeOptions={[
            { value: 20, label: '20 per page' },
            { value: 50, label: '50 per page' },
            { value: 100, label: '100 per page' },
          ]}
        />
        {activeModal && (
          <Modal
            onClose={onModalClose}
            shouldSetActiveList={activeModal !== 'createAndAdd'}
            type={view}
            crmType={crmTypeRef.current}
            fullList
          />
        )}
      </Container>
    </Pane>
  );
}

export default Lists;
